import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@readsavor/api-client';
import { useAuthStore, userQueryKeys } from '@readsavor/auth';
/**
 * Provides a mutation for renewing a user's subscription.
 *
 * This hook simplifies the API call for the UI layer. Similar to the user
 * initialization hook, it now includes an `onSuccess` callback to manually
 * invalidate and refetch all user-related queries. This is necessary because
 * some backend operations might not trigger real-time updates reliably,
 * ensuring the UI reflects the new subscription state immediately.
 *
 * UI components can pass their own `onSuccess`, `onError` callbacks to handle
 * feedback like toast notifications.
 *
 * @example
 * const { mutate: renew, isPending } = useSubscriptionRenewal({
 *   onSuccess: () => showToast('Renewal successful!'),
 *   onError: (error) => showToast(`Renewal failed: ${error.message}`),
 * });
 *
 * renew({ idempotencyKey: 'unique-key-for-this-action' });
 */
export const useSubscriptionRenewal = (options) => {
    const queryClient = useAuthStore(state => state.queryClient);
    return useMutation({
        mutationFn: (payload) => apiClient.integration.renewSubscription(payload),
        onSuccess: (data, variables, context) => {
            // Invalidate and refetch all user-related data to ensure UI consistency
            // after a significant state change like a subscription renewal.
            if (queryClient) {
                queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
                queryClient.refetchQueries({ queryKey: userQueryKeys.all });
            }
            else {
                console.warn('[useSubscriptionRenewal] QueryClient not found in auth store. Skipping automatic refetch.');
            }
            // Call the original onSuccess if it was provided
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context);
            }
        },
        ...options,
    });
};
//# sourceMappingURL=useSubscriptionRenewal.js.map